import { $dfs } from '@lexical/utils';
import { $getEditor } from 'lexical';
import { confirmLexicalStateInTextComponent } from 'modules/draftjs2lexical';
import { $setEditorStateFromHtmlString } from 'modules/Lexical/Plugins/HtmlContentPlugin';
import * as Models from 'models';
import { createLexicalTextEditor } from '../../editors';
import { $createReferenceCitationNode, $isReferenceCitationNode } from '../../nodes/ReferenceCitationNode';
import { REFERENCE_CITATION_MARKER } from '../nodes/ReferenceCitationNodeWithExportDom';
import { shouldInheritInlineStyles } from './shouldInheritInlineStyles';

const $processReferenceCitations = (
  referenceCitationIds: string[],
  getActualReferenceCitationId: (referenceId: string) => string,
): string[] => {
  const actualReferenceCitationIds: string[] = [];

  const referenceCitationIdsLength = referenceCitationIds.length;
  let referenceCitationIdsIndex = 0;
  const nodesArray = $dfs();
  nodesArray.forEach((dfsNode) => {
    const textContent = dfsNode.node.getTextContent();

    if (textContent.trim() === REFERENCE_CITATION_MARKER) {
      const referenceCitationId = referenceCitationIdsIndex < referenceCitationIdsLength ?
        referenceCitationIds[referenceCitationIdsIndex]
        : '';
      const actualReferenceCitationId = getActualReferenceCitationId(referenceCitationId);

      if (actualReferenceCitationId) {
        actualReferenceCitationIds.push(actualReferenceCitationId);
        dfsNode.node.replace($createReferenceCitationNode(actualReferenceCitationId));
      }

      referenceCitationIdsIndex++;
    } else if ($isReferenceCitationNode(dfsNode.node)) {
      const erf = dfsNode.node;
      const actualReferenceCitationId = getActualReferenceCitationId(erf.getReferenceId());

      erf.setReferenceId(actualReferenceCitationId);
      actualReferenceCitationIds.push(actualReferenceCitationId);

      referenceCitationIdsIndex++;
    }
  });

  return actualReferenceCitationIds;
};

const $processInheritedReferenceCitations = (
  getActualReferenceCitationId: (referenceId: string) => string,
): string[] => {
  const actualReferenceCitationIds: string[] = [];

  const nodesArray = $dfs();
  nodesArray.forEach((dfsNode) => {
    if ($isReferenceCitationNode(dfsNode.node)) {
      const erf = dfsNode.node;
      const actualReferenceCitationId = getActualReferenceCitationId(erf.getReferenceId());

      erf.setReferenceId(actualReferenceCitationId);
      actualReferenceCitationIds.push(actualReferenceCitationId);
    }
  });

  return actualReferenceCitationIds;
};

export const restoreLexicalStateAndReferencesFromText = (
  textComponent: Models.TranslatedTextComponent,
  sourceTextComponent: Models.TextComponent,
  getActualReferenceCitationId: (referenceId: string) => string,
  relationStyles?: Models.TextRelationStyles,
): Models.TranslatedTextComponent => {
  const result = structuredClone(textComponent);
  let sourceTextComponentWithLexicalState = structuredClone(sourceTextComponent);
  const { text: textContent, referenceCitations: referenceCitationIds } = result;

  if (!textContent) {
    return result;
  }

  if (!sourceTextComponent?.lexicalState) {
    sourceTextComponentWithLexicalState = confirmLexicalStateInTextComponent(sourceTextComponent, relationStyles ?? null);
  }

  const sourceEditor = createLexicalTextEditor(sourceTextComponentWithLexicalState?.lexicalState ?? '');
  const canInheritStyles = shouldInheritInlineStyles(sourceEditor, textContent);
  result.canInheritStyles = canInheritStyles;

  const editor = createLexicalTextEditor();
  if (canInheritStyles) {
    editor.update(() => {
      $getEditor().setEditorState(sourceEditor.getEditorState());
    }, { discrete: true });

    editor.update(() => {
      const actualReferenceCitationIds = referenceCitationIds?.length
        ? $processInheritedReferenceCitations(getActualReferenceCitationId)
        : [];

      result.referenceCitations = actualReferenceCitationIds;
    }, { discrete: true });
  } else {
    editor.update(() => {
      $setEditorStateFromHtmlString(textContent);

      const actualReferenceCitationIds = referenceCitationIds?.length
        ? $processReferenceCitations(referenceCitationIds, getActualReferenceCitationId)
        : [];

      result.referenceCitations = actualReferenceCitationIds;
    }, { discrete: true });
  }

  result.lexicalState = JSON.stringify(editor.getEditorState());

  // Clean text content if no reference citations
  if (!referenceCitationIds?.length) {
    result.text = textContent.replaceAll(REFERENCE_CITATION_MARKER, '');
  }

  return result;
};
